import { useState } from "react";
import { motion, AnimatePresence } from "motion/react";
import GlareHover from "../../components/GlareHover.jsx";

interface ClientPartner {
  name: string;
  logo: string;
}

const clients: ClientPartner[] = [
  { name: "Bank of Baroda", logo: "/partners/bank-of-baroda.png" },
  {
    name: "Madhya Pradesh Tourism",
    logo: "/partners/madhya-pradesh-tourism.png",
  },
  { name: "Uttarakhand Tourism", logo: "/partners/uttarakhand-tourism.png" },
  {
    name: "Ministry of Electronics & IT",
    logo: "/partners/ministry-of-electronics.png",
  },
  { name: "Nippon Mutual Fund", logo: "/partners/nippon-mutual-fund.png" },
  { name: "MPA Goa", logo: "/partners/mpa-goa.png" },
  { name: "Captain of Ports", logo: "/partners/captain-of-ports.png" },
  { name: "Welspun Corp", logo: "/partners/Welspun-corp.png" },
];

const INITIAL_CLIENTS_COUNT = 8;

export default function Partners() {
  const [isExpanded, setIsExpanded] = useState(false);
  const [activeCard, setActiveCard] = useState<number | null>(null);
  const visibleClients = isExpanded
    ? clients
    : clients.slice(0, INITIAL_CLIENTS_COUNT);
  const hasMore = clients.length > INITIAL_CLIENTS_COUNT;

  return (
    <section className="w-full relative z-10">
      <div className="w-full max-w-[1280px] mx-auto px-4 md:px-6 mb-16 md:mb-20">
        <motion.div
          initial={{ y: 80, opacity: 0 }}
          whileInView={{ y: 0, opacity: 1 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
          className="font-Montserrat text-xs tracking-widest uppercase text-indigo-600 dark:text-indigo-300/70 mb-6 flex items-center gap-4"
        >
          <div className="w-4 h-[1px] bg-indigo-500/50" />
          <span>Our Clients</span>
        </motion.div>

        <motion.h2
          initial={{ y: 80, opacity: 0 }}
          whileInView={{ y: 0, opacity: 1 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{
            duration: 0.8,
            ease: [0.16, 1, 0.3, 1],
            delay: 0.15,
          }}
          className="font-Montserrat text-4xl md:text-6xl lg:text-7xl font-bold tracking-tight text-black dark:text-white"
        >
          Selected{" "}
          <span className="text-transparent bg-clip-text bg-gradient-to-r from-black dark:from-white via-indigo-600 dark:via-indigo-200 to-indigo-500/50 italic font-serif">
            Partners
          </span>
        </motion.h2>
      </div>
      <div className="w-full max-w-[1280px] mx-auto px-4 md:px-6">
        <div
          className="grid grid-cols-2 md:grid-cols-4 border-t border-l border-black/5 dark:border-white/5"
        >
          <AnimatePresence initial={false}>
            {visibleClients.map((client, i) => {
              const isActive = activeCard === i;
              return (
                <motion.div
                  key={client.name}
                  initial={{ y: 30, opacity: 0 }}
                  animate={{ y: 0, opacity: 1 }}
                  exit={{ y: 20, opacity: 0 }}
                  transition={{
                    duration: 0.5,
                    ease: [0.16, 1, 0.3, 1],
                    delay: (i % 4) * 0.05,
                  }}
                  onClick={() => setActiveCard(isActive ? null : i)}
                  className={`group relative border-b border-black/5 dark:border-white/5 overflow-hidden transition-colors duration-500 cursor-pointer select-none ${
                    isActive
                      ? "bg-black/[0.04] dark:bg-white/[0.05]"
                      : "hover:bg-black/[0.02] dark:hover:bg-white/[0.03]"
                  } ${
                    (i + 1) % 4 !== 0 ? "border-r" : ""
                  }`}
                >
                  <GlareHover
                    width="100%"
                    height="100%"
                    borderRadius="0"
                    borderColor="transparent"
                    glareColor="#6366f1"
                    glareOpacity={0.15}
                    glareSize={220}
                    glareAngle={-45}
                    transitionDuration={800}
                    className="relative flex items-center justify-center"
                    style={{
                      background: "transparent",
                      padding: "0",
                      aspectRatio: "3 / 2",
                    }}
                  >
                    {/* Subtle radial backdrop glow on hover / tap */}
                    <div
                      className={`absolute inset-0 transition-opacity duration-700 pointer-events-none bg-[radial-gradient(ellipse_at_center,rgba(99,102,241,0.12)_0%,transparent_70%)] ${
                        isActive ? "opacity-100" : "opacity-0 group-hover:opacity-100"
                      }`}
                    />

                    {/* Brand Logo with smooth scale and color reveal */}
                    <img
                      src={client.logo}
                      alt={client.name}
                      onError={(e) => {
                        const target = e.currentTarget;
                        if (target.src.endsWith(".png")) {
                          target.src = target.src.replace(/\.png$/, ".jpg");
                        } else if (target.src.endsWith(".jpg")) {
                          target.src = target.src.replace(/\.jpg$/, ".svg");
                        } else {
                          target.style.display = "none";
                          const fallback = target.nextElementSibling;
                          if (fallback) fallback.classList.remove("hidden");
                        }
                      }}
                      className={`relative z-10 w-full h-full object-contain p-3 sm:p-4 md:p-5 transition-all duration-500 ease-[cubic-bezier(0.16,1,0.3,1)] filter ${
                        isActive
                          ? "grayscale-0 opacity-100 scale-105"
                          : "grayscale opacity-50 group-hover:grayscale-0 group-hover:opacity-100 group-hover:scale-105"
                      }`}
                    />

                    {/* Text fallback if logo fails to load */}
                    <span className="hidden font-Montserrat text-sm md:text-base font-bold tracking-wider uppercase text-black/70 dark:text-white/80 group-hover:text-indigo-500 transition-colors duration-300 text-center px-4">
                      {client.name}
                    </span>
                  </GlareHover>
                </motion.div>
              );
            })}
          </AnimatePresence>
        </div>
      </div>

      {hasMore && (
        <div className="mt-12 flex justify-center">
          <motion.button
            onClick={() => setIsExpanded(!isExpanded)}
            whileHover={{ scale: 1.04 }}
            whileTap={{ scale: 0.96 }}
            className="group interactive flex items-center gap-3 px-8 py-4 rounded-full border border-black/15 dark:border-white/15 bg-white/40 dark:bg-white/5 backdrop-blur-md text-xs font-Montserrat font-semibold uppercase tracking-widest text-black dark:text-white hover:border-indigo-500/50 hover:bg-black/5 dark:hover:bg-white/10 transition-all duration-300 shadow-lg"
          >
            <span>
              {isExpanded
                ? "Show Less"
                : `View All (+${clients.length - INITIAL_CLIENTS_COUNT} more)`}
            </span>
            <motion.svg
              animate={{ rotate: isExpanded ? 180 : 0 }}
              transition={{ duration: 0.3, ease: [0.16, 1, 0.3, 1] }}
              width="14"
              height="14"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="m6 9 6 6 6-6" />
            </motion.svg>
          </motion.button>
        </div>
      )}
    </section>
  );
}
